import { addSavedOffer } from '@cian/favorites-assign-widget';
import { v4 as uuid } from 'uuid';

import { TThunkAction } from '../../types/redux';
import { addToFavorite as addToFavoriteService } from '../../services/favorites';
import { finishFavoriteUpdate, rollbackFavoriteUpdate, startFavoriteUpdate } from '../../actions/recommendations';
import { trackAddToFavorite } from './tracking';
import { TFavoriteActionParameters } from './types';

export const ADD_TO_FAVORITE_REQUEST_ERROR = 'add-to-favorite-request-error' as const;

export function isAddToFavoriteRequestError(error: unknown): error is { code: typeof ADD_TO_FAVORITE_REQUEST_ERROR } {
  return typeof error === 'object' && error !== null && 'code' in error && error.code === ADD_TO_FAVORITE_REQUEST_ERROR;
}

class AddToFavoriteRequestError extends Error {
  public readonly code = ADD_TO_FAVORITE_REQUEST_ERROR;

  public constructor(error: unknown) {
    super(error instanceof Error ? error.message : 'Failed to add an offer to favorites');
    this.name = 'AddToFavoriteRequestError';
  }
}

export function addToFavorite(parameters: TFavoriteActionParameters): TThunkAction<Promise<void>> {
  return async (dispatch, getState, context) => {
    const { recommendation } = parameters;
    const requestId = uuid();

    dispatch(startFavoriteUpdate({ offerId: recommendation.offerId, isFavorite: true, requestId }));

    let response: Awaited<ReturnType<typeof addToFavoriteService>>;

    try {
      response = await addToFavoriteService(context, {
        dealType: recommendation.dealType,
        offerId: recommendation.offerId,
        offerType: recommendation.offerType,
      });
    } catch (error) {
      dispatch(rollbackFavoriteUpdate({ offerId: recommendation.offerId, isFavorite: false, requestId }));

      throw new AddToFavoriteRequestError(error);
    }

    try {
      trackAddToFavorite(parameters);

      const isCurrentFavoriteUpdate =
        getState().recommendations.favoriteUpdateRequestIds?.[recommendation.offerId] === requestId;

      if (!isCurrentFavoriteUpdate) {
        return;
      }

      const folder = response.folder;
      const savedInFolders =
        folder && folder.name && folder.id
          ? [
              {
                id: folder.id,
                name: folder.name,
              },
            ]
          : null;

      dispatch(
        addSavedOffer({
          offerIds: [recommendation.offerId],
          savedInFolders,
          userHasFolders: Boolean(response.hasFolders),
        }),
      );
    } finally {
      dispatch(finishFavoriteUpdate({ offerId: recommendation.offerId, requestId }));
    }
  };
}
